// Smart Daara - Quiz Taker Page for Students
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { quizAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, AlertCircle,
  Play, Trophy, RefreshCw, BookOpen, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

export default function QuizTaker() {
  const { quizId, courseId } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [answers, setAnswers] = useState({}); // {questionId: {answer, isCorrect}}
  const [submitting, setSubmitting] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastFeedback, setLastFeedback] = useState(null);

  useEffect(() => {
    loadQuiz();
  }, [quizId]);

  const loadQuiz = async () => {
    try {
      const quizRes = await quizAPI.getQuiz(quizId);
      setQuiz(quizRes.data);
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    setLoading(true);
    try {
      const res = await quizAPI.startQuiz(quizId);
      setAttemptId(res.data.attempt_id);
      setQuestions(res.data.questions);
      setCurrentIndex(0);
      setAnswers({});
      setQuizComplete(false);
      setResults(null);
    } catch (error) {
      console.error('Error starting quiz:', error);
      toast.error(language === 'fr' ? 'Erreur de démarrage' : 'Start error');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!selectedAnswer) {
      toast.error(language === 'fr' ? 'Sélectionnez une réponse' : 'Select an answer');
      return;
    }

    setSubmitting(true);
    try {
      const currentQuestion = questions[currentIndex];
      const res = await quizAPI.submitAnswer(attemptId, currentQuestion.id, selectedAnswer);
      
      setLastFeedback(res.data);
      setShowFeedback(true);
      
      // Store answer
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: {
          answer: selectedAnswer,
          isCorrect: res.data.is_correct
        }
      }));

      // Show toast
      if (res.data.is_correct) {
        toast.success('🎉 ' + (language === 'fr' ? res.data.message : res.data.message_en));
      } else {
        toast.error('❌ ' + (language === 'fr' ? res.data.message : res.data.message_en));
      }

      // Auto advance after 1.5 seconds
      setTimeout(() => {
        setShowFeedback(false);
        setSelectedAnswer('');
        
        if (currentIndex < questions.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          completeQuiz();
        }
      }, 1500);
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error(language === 'fr' ? 'Erreur de soumission' : 'Submission error');
    } finally {
      setSubmitting(false);
    }
  };

  const completeQuiz = async () => {
    setLoading(true);
    try {
      const res = await quizAPI.completeQuiz(attemptId);
      setResults(res.data);
      setQuizComplete(true);

      if (res.data.is_passed) {
        // Trigger confetti
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } catch (error) {
      console.error('Error completing quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  if (loading && !quiz) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Quiz intro screen
  if (!attemptId && !quizComplete) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="quiz-intro">
        <Button variant="ghost" onClick={() => navigate(`/student/course/${courseId}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Retour au cours' : 'Back to course'}
        </Button>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-emerald-700" />
            </div>
            
            <h1 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
              {quiz?.title_fr || quiz?.title}
            </h1>
            <p className="text-slate-500 mb-6">
              {quiz?.description_fr || quiz?.description || 
                (language === 'fr' ? 'Testez vos connaissances' : 'Test your knowledge')}
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8 text-center">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{quiz?.questions_per_attempt || 10}</p>
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Questions' : 'Questions'}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-700">{quiz?.passing_score || 80}%</p>
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Score requis' : 'Required Score'}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">
                  {Math.ceil((quiz?.questions_per_attempt || 10) * (quiz?.passing_score || 80) / 100)}
                </p>
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Bonnes rép. min' : 'Min correct'}
                </p>
              </div>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg mb-6 text-left">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">
                    {language === 'fr' ? 'Instructions' : 'Instructions'}
                  </p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>{language === 'fr' ? 'Répondez à chaque question' : 'Answer each question'}</li>
                    <li>{language === 'fr' ? 'La bonne réponse ne sera pas révélée' : 'The correct answer will not be revealed'}</li>
                    <li>{language === 'fr' ? `Vous devez obtenir au moins ${quiz?.passing_score || 80}% pour réussir` : `You need at least ${quiz?.passing_score || 80}% to pass`}</li>
                    <li>{language === 'fr' ? 'Vous pouvez réessayer si vous échouez' : 'You can retry if you fail'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button 
              size="lg"
              className="bg-emerald-700 hover:bg-emerald-800 px-8"
              onClick={startQuiz}
              disabled={loading}
              data-testid="start-quiz-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Play className="w-5 h-5 mr-2" />
              )}
              {language === 'fr' ? 'Commencer le devoir' : 'Start Quiz'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz results screen
  if (quizComplete && results) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="quiz-results">
        <Card className="border-0 shadow-lg overflow-hidden">
          <div className={`p-6 ${results.is_passed ? 'bg-emerald-600' : 'bg-red-500'}`}>
            <div className="text-center text-white">
              {results.is_passed ? (
                <Trophy className="w-16 h-16 mx-auto mb-4" />
              ) : (
                <XCircle className="w-16 h-16 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold mb-2">
                {results.is_passed 
                  ? (language === 'fr' ? 'Félicitations!' : 'Congratulations!')
                  : (language === 'fr' ? 'Pas encore...' : 'Not yet...')
                }
              </h2>
              <p className="text-white/90">
                {language === 'fr' ? results.message : results.message_en}
              </p>
            </div>
          </div>

          <CardContent className="p-8">
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-4xl font-bold text-slate-900">
                  {Math.round(results.score_percentage)}%
                </p>
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Votre score' : 'Your score'}
                </p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-4xl font-bold text-emerald-700">
                  {results.correct_count}/{results.total_questions}
                </p>
                <p className="text-sm text-slate-500">
                  {language === 'fr' ? 'Bonnes réponses' : 'Correct answers'}
                </p>
              </div>
            </div>

            {!results.is_passed && (
              <div className="bg-amber-50 p-4 rounded-lg mb-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    {language === 'fr' 
                      ? 'Nous vous conseillons fortement de revoir les leçons avant de réessayer.'
                      : 'We strongly recommend reviewing the lessons before trying again.'
                    }
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/student/course/${courseId}`)}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Revoir les leçons' : 'Review lessons'}
              </Button>
              
              {!results.is_passed && (
                <Button
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                  onClick={() => {
                    setAttemptId(null);
                    setQuestions([]);
                    setQuizComplete(false);
                    setResults(null);
                  }}
                  data-testid="retry-quiz-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Réessayer' : 'Try Again'}
                </Button>
              )}

              {results.is_passed && (
                <Button
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                  onClick={() => navigate(`/student/course/${courseId}`)}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Continuer le cours' : 'Continue course'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz taking screen
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="quiz-taking">
      {/* Progress header */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">
              {language === 'fr' ? 'Question' : 'Question'} {currentIndex + 1}/{questions.length}
            </span>
            <span className="text-sm font-medium text-emerald-700">
              {Object.values(answers).filter(a => a.isCorrect).length} {language === 'fr' ? 'bonnes réponses' : 'correct'}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>

      {/* Question card */}
      <Card className={`border-0 shadow-lg transition-all ${
        showFeedback 
          ? (lastFeedback?.is_correct ? 'ring-4 ring-emerald-200' : 'ring-4 ring-red-200')
          : ''
      }`}>
        <CardContent className="p-8">
          {/* Feedback overlay */}
          {showFeedback && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              lastFeedback?.is_correct 
                ? 'bg-emerald-100 text-emerald-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {lastFeedback?.is_correct ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <XCircle className="w-6 h-6" />
              )}
              <span className="font-semibold">
                {language === 'fr' ? lastFeedback?.message : lastFeedback?.message_en}
              </span>
            </div>
          )}

          <h2 className="text-xl font-semibold text-slate-900 mb-6">
            {currentQuestion?.question_fr || currentQuestion?.question}
          </h2>

          <RadioGroup 
            value={selectedAnswer} 
            onValueChange={setSelectedAnswer}
            disabled={showFeedback}
            className="space-y-3"
          >
            {['a', 'b', 'c', 'd'].map(opt => (
              <div
                key={opt}
                className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedAnswer === opt 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                } ${showFeedback ? 'pointer-events-none opacity-75' : ''}`}
                onClick={() => !showFeedback && setSelectedAnswer(opt)}
                data-testid={`answer-${opt}`}
              >
                <RadioGroupItem value={opt} id={opt} className="sr-only" />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 font-semibold ${
                  selectedAnswer === opt 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {opt.toUpperCase()}
                </div>
                <Label htmlFor={opt} className="flex-1 cursor-pointer text-slate-700">
                  {currentQuestion?.[`option_${opt}_fr`] || currentQuestion?.[`option_${opt}`]}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {!showFeedback && (
            <Button
              className="w-full mt-6 bg-emerald-700 hover:bg-emerald-800"
              size="lg"
              onClick={submitAnswer}
              disabled={!selectedAnswer || submitting}
              data-testid="submit-answer-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              {language === 'fr' ? 'Valider ma réponse' : 'Submit Answer'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
